import { useContext } from 'react';

import { Text, SimpleGrid, Box, Button } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import Divider from 'views/home_form/components/Divider';

import {
  changeDwellingMortgagee,
  addDwellingMortgagee,
  removeDwellingMortgagee,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import { FILE_TYPE, APPROVED_LENDERS } from 'variables/constants';

import isSuperAdmin from 'helpers/isSuperAdmin/isSuperAdmin';

import { AuthContext } from 'contexts/AuthContext';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface MortgageeProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const Mortgagee = ({ dwelling, index }: MortgageeProps) => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const superAdmin = isSuperAdmin(user);

  const insuranceCompany = useAppSelector(
    (state) => state.homePersonalForm.data.policy_information.insurance_company,
  );

  const onMortgageeChange = (
    event: any,
    mortgageeIndex: number,
    field: string,
  ) => {
    const { value } = event.target;

    dispatch(
      changeDwellingMortgagee({
        dwellingIndex: index,
        mortgageeIndex,
        name: field,
        value,
      }),
    );
  };

  const handleAddMortgagee = () => {
    if (!dwelling.mortgagee || dwelling.mortgagee.length < 3) {
      dispatch(addDwellingMortgagee(index));
    }
  };

  const handleRemoveMortgagee = (mortgageeIndex: number) => {
    dispatch(removeDwellingMortgagee({ dwellingIndex: index, mortgageeIndex }));
  };

  const requiresRemarks = (name: string) => {
    const normalizedName = (name || '').trim();

    if (!normalizedName) return false;

    // For Max Insurance any lender is allowed
    if (insuranceCompany.toLowerCase().includes('max')) {
      return false;
    }

    const lowerCaseName = normalizedName.toLowerCase();

    return !Object.keys(APPROVED_LENDERS).some(
      (key) => key.toLowerCase() === lowerCaseName,
    );
  };

  return (
    <Box mt="20px">
      <Divider mb="15px">Mortgagee Information / Loss Payee</Divider>

      {dwelling.mortgagee && dwelling.mortgagee.length > 0 ? (
        <>
          {dwelling.mortgagee.map((mortgagee, mortgageeIndex: number) => (
            <Box
              key={mortgageeIndex}
              mt={mortgageeIndex === 0 ? '0' : '15px'}
              p="15px"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="15px"
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.600">
                  Mortgagee {mortgageeIndex + 1}
                </Text>
                {superAdmin && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => handleRemoveMortgagee(mortgageeIndex)}
                  >
                    Remove
                    <Text textColor="red" display="inline" ml="5px">
                      (dev mode)
                    </Text>
                  </Button>
                )}
              </Box>

              <SimpleGrid columns={6} spacing="15px" mb="15px">
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Name"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_name_${mortgageeIndex}`}
                  value={mortgagee.name || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'name')
                  }
                  message={{
                    validationId: 'mortgagee_name',
                    storageKey: 'mortgagee',
                    index: mortgageeIndex,
                    manual: {
                      show: requiresRemarks(mortgagee.name),
                      message:
                        'Any risk submitted with a non-standard mortgage lender not on the National Housing Act Approved Lenders list requires market underwriting approval with the exception of Max Insurance.',
                    },
                    file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
                    compact: true,
                  }}
                  required
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Address"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_address_${mortgageeIndex}`}
                  value={mortgagee.address || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'address')
                  }
                  required
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Nature of Interest"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_nature_of_interest_${mortgageeIndex}`}
                  value={mortgagee.nature_of_interest || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'nature_of_interest')
                  }
                  required
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="City"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_city_${mortgageeIndex}`}
                  value={mortgagee.city || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'city')
                  }
                  required
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Province Code"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_province_code_${mortgageeIndex}`}
                  value={mortgagee.province_code || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'province_code')
                  }
                  required
                />
                <FormInput
                  section={SECTIONS.DWELLINGS}
                  label="Postal Code"
                  subLabel={`Mortgagee ${mortgageeIndex + 1}`}
                  name={`mortgagee_postal_code_${mortgageeIndex}`}
                  value={mortgagee.postal_code || ''}
                  onChange={(e: any) =>
                    onMortgageeChange(e, mortgageeIndex, 'postal_code')
                  }
                  required
                />
              </SimpleGrid>
            </Box>
          ))}
        </>
      ) : (
        <Text fontSize="md" color="gray.600" fontStyle="italic">
          No mortgagee information added
        </Text>
      )}

      {superAdmin && (!dwelling.mortgagee || dwelling.mortgagee.length < 3) && (
        <Box mt="15px">
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={handleAddMortgagee}
          >
            Add Mortgagee ({dwelling.mortgagee.length || 0}/3)
            <Text textColor="red" display="inline" ml="10px">
              (dev mode)
            </Text>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Mortgagee;
